import { Suspense, useCallback, useEffect, useRef, type FC } from 'react';
import { fetchQuery, useFragment, useQueryLoader, useRelayEnvironment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';

import { getGuestId } from 'dibs-cookie-jar';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';

import { useHomePageContext } from '../HomePageContext.tsx';
import {
    shouldUseFull2TowerPersonalizedVariant,
    trackFull2TowerPersonalizedTest,
    YOU_WANT_RECOMMENDATION_COUNT,
} from '../helpers/full2TowerPersonalizedTestHelpers.ts';
import { buildItemsGridDataQuery } from './buildItemsGridData.ts';
import { type HpSharedItemsGridTrendingSeedQuery } from './__generated__/HpSharedItemsGridTrendingSeedQuery.graphql.ts';
import { type HpSharedItemsGrid_user$key } from './__generated__/HpSharedItemsGrid_user.graphql.ts';
import { getRecentlyViewedItemIds } from './getRecentlyViewedItemIds.ts';
import {
    getFull2TowerPersonalizedRecommendationSlices,
    getHpSharedItemsGridInitialFetchCounts,
} from './constants.ts';
import { HpSharedItemsGridShimmer } from './HpSharedItemsGridShimmer.tsx';
import { type buildItemsGridDataQuery as BuildItemsGridDataQuery } from './__generated__/buildItemsGridDataQuery.graphql.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';

const userFragment = graphql`
    fragment HpSharedItemsGrid_user on User
    @argumentDefinitions(
        recommendationType: { type: "UserRecommendationType!", defaultValue: PERSONALIZED }
    ) {
        twoTowerModelItemRecommendations(type: $recommendationType) {
            recommendationIds
        }
    }
`;

const HpSharedItemsGridContent = dibsLazyClient(
    () =>
        import(/* webpackChunkName: "HpSharedItemsGridContent" */ './HpSharedItemsGridContent.tsx')
);

type HpSharedItemsGridProps = {
    user: HpSharedItemsGrid_user$key | null;
    userId: string | null;
    fetchFavorites: boolean;
};

export const HpSharedItemsGrid: FC<HpSharedItemsGridProps> = ({
    user: userRef,
    userId,
    fetchFavorites,
}) => {
    const user = useFragment(userFragment, userRef);
    const [queryRef, loadQuery] = useQueryLoader<BuildItemsGridDataQuery>(buildItemsGridDataQuery);
    const environment = useRelayEnvironment();
    const gridRef = useRef(null);
    const loadMoreRecommendationItemIdsRef = useRef<string[]>([]);
    const { priceBookName, isTrade, userType, isMobile } = useHomePageContext();
    const isClient = useClientState();
    const isVisibleRef = useRef(false);
    const hasLoadedRef = useRef(false);
    const loadedVariantRef = useRef<boolean | null>(null);
    const loadRequestIdRef = useRef(0);

    const loadGridQuery = useCallback(async (): Promise<void> => {
        const requestId = ++loadRequestIdRef.current;

        // Guests load immediately. Logged-in users wait for the user fragment so
        // recommendation eligibility / AB exposure is correct on first load.
        if (userId && user === null) {
            return;
        }

        let recentlyViewedItemIds = getRecentlyViewedItemIds();

        if (recentlyViewedItemIds.length < 4) {
            const trendingResult = await fetchQuery<HpSharedItemsGridTrendingSeedQuery>(
                environment,
                graphql`
                    query HpSharedItemsGridTrendingSeedQuery {
                        viewer {
                            itemSearchTrendingItems(
                                sellerVerticals: ["jewelry", "furniture", "fashion", "art"]
                            ) {
                                itemIds
                            }
                        }
                    }
                `,
                {},
                { fetchPolicy: 'store-or-network' }
            ).toPromise();
            recentlyViewedItemIds =
                trendingResult?.viewer?.itemSearchTrendingItems?.itemIds?.filter(filterFalsy) || [];
        }

        if (requestId !== loadRequestIdRef.current) {
            return;
        }

        const recommendationIds = (
            user?.twoTowerModelItemRecommendations?.recommendationIds || []
        ).filter(filterFalsy);
        const hasRecommendations = recommendationIds.length > 0;
        const useFull2TowerPersonalizedVariant =
            shouldUseFull2TowerPersonalizedVariant(hasRecommendations);

        if (hasLoadedRef.current && loadedVariantRef.current === useFull2TowerPersonalizedVariant) {
            return;
        }

        if (hasRecommendations) {
            trackFull2TowerPersonalizedTest();
        }

        hasLoadedRef.current = true;
        loadedVariantRef.current = useFull2TowerPersonalizedVariant;

        let recommendationItemIds: string[] = [];
        loadMoreRecommendationItemIdsRef.current = [];

        if (useFull2TowerPersonalizedVariant) {
            const slices = getFull2TowerPersonalizedRecommendationSlices(
                recommendationIds,
                isMobile,
                YOU_WANT_RECOMMENDATION_COUNT
            );
            recommendationItemIds = [...slices.initialItemIds, ...slices.fallbackItemIds];
            loadMoreRecommendationItemIdsRef.current = slices.loadMoreItemIds;
        }

        loadQuery({
            userCountryCode: getUserSessionCountryCode(),
            priceBookName,
            isTrade,
            consentedBuyerId: userId,
            guestId: getGuestId(document.cookie),
            recentlyViewedItemIds,
            homepageId: '',
            userType,
            abTestList: [],
            isFull2TowerPersonalizedVariant: useFull2TowerPersonalizedVariant,
            recommendationItemIds,
            ...(useFull2TowerPersonalizedVariant
                ? {
                      editorsPicksCount: 0,
                      mostSavedCount: 0,
                      featuredItemsCount:
                          getHpSharedItemsGridInitialFetchCounts(isMobile).featuredItemsCount,
                  }
                : getHpSharedItemsGridInitialFetchCounts(isMobile)),
        });
    }, [environment, isMobile, isTrade, loadQuery, priceBookName, user, userType, userId]);

    useEffect(() => {
        if (isClient && isVisibleRef.current) {
            void loadGridQuery();
        }
    }, [isClient, user, userId, loadGridQuery]);

    return (
        <div ref={gridRef}>
            {isClient ? (
                <VisibilityTracker
                    elementRef={gridRef}
                    onVisibilityChange={({ isVisible }) => {
                        isVisibleRef.current = isVisible;
                        if (isVisible) {
                            void loadGridQuery();
                        }
                    }}
                    observerOptions={{
                        rootMargin: isMobile ? '700px 0px' : '400px 0px',
                    }}
                />
            ) : null}
            <div
                className={classnames(dibsCss.mbLarge, dibsCss.mtLarger, dibsCss.textCenter, {
                    [dibsCss.sassyFontHeaderLarge]: isMobile,
                    [dibsCss.sassyFontHeaderXxLarge]: !isMobile,
                })}
            >
                <FormattedMessage
                    id="abm.HpSharedItemsGrid.title"
                    defaultMessage="Curated <italicWrapper>Just for You</italicWrapper>"
                    values={{
                        italicWrapper: chunks => (
                            <span
                                className={
                                    isMobile
                                        ? dibsCss.sassyFontHeaderLargeItalic
                                        : dibsCss.sassyFontHeaderXxLargeItalic
                                }
                            >
                                {chunks}
                            </span>
                        ),
                    }}
                />
            </div>
            {!queryRef ? <HpSharedItemsGridShimmer /> : null}
            {queryRef ? (
                <Suspense fallback={<HpSharedItemsGridShimmer />}>
                    <HpSharedItemsGridContent
                        queryRef={queryRef}
                        userId={userId}
                        fetchFavorites={fetchFavorites}
                        loadMoreRecommendationItemIds={loadMoreRecommendationItemIdsRef.current}
                    />
                </Suspense>
            ) : null}
        </div>
    );
};
